import { Alert, Box, Button, CircularProgress, Typography } from "@mui/material";
import type { FC } from "react";
import { useEffect } from "react";
import Markdown from "react-markdown";
import markdownTOC from "remark-toc";
import { useAuth } from "../../../contexts/AuthProvider";
import { useAnalytics } from "../../../contexts/analytics/hooks";
import { useAdminGuide } from "../../../hooks/useAdminGuide";
import { components } from "../utils";

export const AdminUserGuides: FC = () => {
	const { userInfo } = useAuth();
	const { track } = useAnalytics();

	const { data, isLoading, isError, error, refetch } = useAdminGuide();

	useEffect(() => {
		track("user_guide_opened", {
			user_id: userInfo?.id,
			guide_type: "admin",
			is_admin: true,
			role: userInfo?.role,
		});
	}, [track, userInfo?.id, userInfo?.role]);

	if (isLoading) {
		return (
			<Box
				component="main"
				sx={{
					maxHeight: "100%",
					width: "100%",
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					padding: 4,
				}}
			>
				<Box sx={{ textAlign: "center" }}>
					<CircularProgress size={48} />
					<Typography variant="body1" sx={{ marginTop: 2 }}>
						Loading admin user guide...
					</Typography>
				</Box>
			</Box>
		);
	}

	if (isError) {
		return (
			<Box
				component="main"
				sx={{
					maxHeight: "100%",
					width: "100%",
					padding: 4,
				}}
			>
				<Alert severity="error" sx={{ marginBottom: 2 }}>
					<Typography variant="h6" gutterBottom>
						Failed to load admin user guide
					</Typography>
					<Typography variant="body2">{error instanceof Error ? error.message : "An unknown error occurred"}</Typography>
					<Button variant="outlined" onClick={() => refetch()} sx={{ marginTop: 2 }}>
						Try Again
					</Button>
				</Alert>
			</Box>
		);
	}

	return (
		<Box component="main" sx={{ maxHeight: "100%", width: "100%", overflowY: "auto" }}>
			<Box sx={{ padding: 3 }}>
				<Markdown components={components} remarkPlugins={[markdownTOC]} urlTransform={(url) => url}>
					{data?.content || ""}
				</Markdown>
			</Box>
		</Box>
	);
};
